/*
  Recording a spoken instruction, with the browser's media APIs injected so this file is testable.

  WHY INJECTION RATHER THAN GLOBALS. `MediaRecorder` and `getUserMedia` do not exist in happy-dom, and a
  microphone is not something a test run can be given. The alternative to injection is a module that can
  only be exercised by a human clicking a button, which is how the four unreachable-surface bugs in this
  project's history got shipped. The seams below are the same shape `chat.ts` uses for the network.

  WHAT CANNOT BE TESTED HERE, and it is worth naming rather than letting the coverage imply otherwise:
  that a real microphone produces audio the service can read. Everything below asserts the DECISIONS —
  which container, when to stop, what happens on a refusal, whether the microphone is released — against
  fakes of my own construction. Nothing here has heard a voice.
*/
import { AZURE_AUDIO_CONTAINERS, MAX_AUDIO_BYTES, type AudioContainer } from '@integrate/contracts';

/** The one thing needed from `MediaRecorder`'s static side. */
export type IsTypeSupported = (mimeType: string) => boolean;

/** The parts of a `MediaStreamTrack` this file touches. */
export interface CaptureTrack {
  stop(): void;
}

/** The parts of a `MediaStream` this file touches. */
export interface CaptureStream {
  getTracks(): CaptureTrack[];
}

/** The parts of a `MediaRecorder` instance this file touches. */
export interface CaptureRecorder {
  start(timesliceMs?: number): void;
  stop(): void;
  ondataavailable: ((event: { data: Blob }) => void) | null;
  onstop: (() => void) | null;
  onerror: ((event: unknown) => void) | null;
}

export interface CaptureEnvironment {
  isTypeSupported: IsTypeSupported;
  /** Resolves with a stream, or rejects — a rejection is usually the person saying no. */
  getStream(): Promise<CaptureStream>;
  createRecorder(stream: CaptureStream, mimeType: string): CaptureRecorder;
}

/** Why a recording did not happen, in words a person can act on. */
export class VoiceUnavailableError extends Error {
  constructor(
    message: string,
    /** `denied` is a choice somebody made; the rest are the environment's problem. */
    readonly kind: 'unsupported' | 'denied' | 'failed' | 'cancelled',
  ) {
    super(message);
    this.name = 'VoiceUnavailableError';
  }
}

/**
 * The container this browser can record that the service will read — or nothing.
 *
 * Returning `null` rather than a default is the point. A default means recording in a format nobody
 * accepts, discovering it after the upload, and telling somebody their two minutes of speech is gone;
 * `null` means the button is never offered, which is a worse feature and a much better experience.
 *
 * Ordered by `AZURE_AUDIO_CONTAINERS`, so the preference — smallest first — lives with the list rather
 * than being re-decided here.
 */
export function pickContainer(isTypeSupported: IsTypeSupported): AudioContainer | null {
  return AZURE_AUDIO_CONTAINERS.find((c) => isTypeSupported(c.mimeType)) ?? null;
}

export interface Recording {
  blob: Blob;
  /** The name the upload must carry: the service reads the extension to decide the format. */
  filename: string;
  mimeType: string;
  /** True when recording stopped because the ceiling was reached rather than because somebody asked. */
  truncated: boolean;
}

export interface CaptureOptions {
  /**
   * Called when the ceiling stopped the recording on its own.
   *
   * Without this the ceiling is unreachable from an interface: recording ends, the microphone light goes
   * out, and the button still says "Stop" until somebody presses it. The notification is what lets the
   * UI say "that is as much as can be sent" at the moment it becomes true.
   */
  onAutoStop?: () => void;
}

/**
 * One recording, from permission to bytes.
 *
 * Deliberately not a hook and not a store: a component wraps this, and the wrapping is trivial, whereas
 * a hook would drag React's lifecycle into the one part of this that has to be right regardless of when
 * a re-render happens — releasing the microphone.
 *
 * THE STATE IS EXPLICIT because every interesting path is about a `stop()` that has been requested and
 * an `onstop` that has not arrived yet — `MediaRecorder` flushes asynchronously, so between those two
 * moments anything can happen. The first version of this file tracked the same thing with three nullable
 * fields, and the ceiling firing before anybody had asked for the result silently threw the recording
 * away — the flavour of bug that only appears when somebody talks for four minutes.
 */
export class VoiceCapture {
  private state: 'idle' | 'recording' | 'stopping' = 'idle';
  private stream: CaptureStream | null = null;
  private recorder: CaptureRecorder | null = null;
  private container: AudioContainer | null = null;
  private chunks: Blob[] = [];
  private bytes = 0;
  private truncated = false;
  /** A finished recording nobody has collected yet — the ceiling can produce one unasked. */
  private pending: Recording | null = null;
  private settle: ((recording: Recording) => void) | null = null;
  private fail: ((error: Error) => void) | null = null;

  constructor(
    private readonly env: CaptureEnvironment,
    private readonly options: CaptureOptions = {},
  ) {}

  /** True when this browser can record something the service accepts. */
  get available(): boolean {
    return pickContainer(this.env.isTypeSupported) !== null;
  }

  get recording(): boolean {
    return this.state === 'recording';
  }

  /**
   * Ask for the microphone and start.
   *
   * The container is chosen BEFORE the permission prompt, so a browser that could never produce an
   * acceptable recording does not ask for the microphone first and disappoint afterwards.
   */
  async start(): Promise<void> {
    if (this.state !== 'idle') return;

    const container = pickContainer(this.env.isTypeSupported);
    if (!container) {
      throw new VoiceUnavailableError(
        'This browser cannot record audio in a format the assistant can read. Type the request instead.',
        'unsupported',
      );
    }

    let stream: CaptureStream;
    try {
      stream = await this.env.getStream();
    } catch (cause) {
      /*
        Almost always somebody clicking "Block", and the message says so rather than reporting a
        `NotAllowedError`. The two other real causes — no microphone attached, and a page not served over
        HTTPS, which silently removes `mediaDevices` altogether — are named too, because between them
        they account for the rest, and a message listing the actual possibilities beats "an error
        occurred" by exactly the amount of time somebody would otherwise spend guessing.
      */
      throw new VoiceUnavailableError(
        'The microphone is not available. Check this site’s microphone permission, and that the page is served over HTTPS.',
        cause instanceof Error && cause.name === 'NotAllowedError' ? 'denied' : 'failed',
      );
    }

    this.stream = stream;
    this.container = container;
    this.chunks = [];
    this.bytes = 0;
    this.truncated = false;
    this.pending = null;

    const recorder = this.env.createRecorder(stream, container.mimeType);
    this.recorder = recorder;
    this.state = 'recording';

    /*
      EVERY HANDLER BELOW IS GUARDED ON `this.recorder === recorder`, and that one line is what makes
      cancel and error safe.

      `stop()` does not flush synchronously — `onstop` arrives later — so a recording that was cancelled
      or that errored still has an `onstop` in flight, and without this guard it lands after the fact and
      quietly resurrects the recording somebody just discarded. `release()` nulls `this.recorder`, so
      identity is the single thing that says "this recorder's turn is over", and it covers all three
      exits at once instead of one flag per exit.
    */
    const mine = () => this.recorder === recorder;

    recorder.ondataavailable = (event) => {
      if (!mine() || !event.data || event.data.size === 0) return;
      this.chunks.push(event.data);
      this.bytes += event.data.size;
      /*
        THE CEILING IS CHECKED AS THE BYTES ARRIVE, not when recording stops, and the difference is not
        academic: in WAV the limit is about four minutes, so a recorder that only checked at the end
        would let somebody talk for ten and then throw all of it away. Stopping early keeps what was said
        up to the limit, and `truncated` lets the caller say so rather than pretending it heard the end.
      */
      if (this.bytes >= MAX_AUDIO_BYTES && this.state === 'recording') {
        this.truncated = true;
        this.requestStop();
        this.options.onAutoStop?.();
      }
    };

    recorder.onstop = () => {
      if (!mine()) return;
      const recording: Recording = {
        blob: new Blob(this.chunks, { type: container.mimeType }),
        filename: `request.${container.extension}`,
        mimeType: container.mimeType,
        truncated: this.truncated,
      };
      const settle = this.settle;
      this.release();
      // Held rather than dropped: the ceiling can finish a recording before anybody has asked for it.
      if (settle) settle(recording);
      else this.pending = recording;
    };

    recorder.onerror = () => {
      if (!mine()) return;
      const fail = this.fail;
      this.release();
      fail?.(new VoiceUnavailableError('The recording failed part-way through.', 'failed'));
    };

    /*
      A timeslice, so `ondataavailable` fires periodically instead of once at the end. Without it the
      ceiling above cannot fire early — the only chunk arrives after the recording is already over — so
      this one argument is what makes that whole check mean anything.
    */
    recorder.start(1000);
  }

  /** Stop and hand back the bytes. Resolves once the recorder has flushed. */
  finish(): Promise<Recording> {
    if (this.pending) {
      const done = this.pending;
      this.pending = null;
      return Promise.resolve(done);
    }
    if (this.state === 'idle') {
      return Promise.reject(new VoiceUnavailableError('Nothing is being recorded.', 'failed'));
    }
    return new Promise<Recording>((resolve, reject) => {
      this.settle = resolve;
      this.fail = reject;
      /*
        `stopping` means the ceiling already asked and the flush is on its way, so this waits rather than
        asking again. It is the ORDINARY sequence, not an edge case: the interface hears `onAutoStop` and
        collects the recording immediately, which lands here microseconds before `onstop` does. Rejecting
        it — which this did until the test caught it — throws away the recording at the exact moment the
        person is being told it is ready.
      */
      if (this.state === 'recording') this.requestStop();
    });
  }

  /**
   * Throw the recording away and release the microphone.
   *
   * Separate from `finish` because "I did not mean to press that" has to be reachable without producing
   * a request, and because a component unmounting mid-recording must leave the microphone off. Anything
   * already captured is dropped here — audio of somebody speaking is not a thing to keep lying around
   * because it might be useful later.
   */
  cancel(): void {
    const fail = this.fail;
    const wasRecording = this.state !== 'idle';
    // Stop first, release second: the release is what tells the in-flight `onstop` to stay out of it.
    this.stopRecorder();
    this.release();
    // A recording already collected is not cancellable, and neither is one that was never started.
    this.pending = null;
    if (wasRecording) fail?.(new VoiceUnavailableError('The recording was cancelled.', 'cancelled'));
  }

  private requestStop(): void {
    this.state = 'stopping';
    this.stopRecorder();
  }

  private stopRecorder(): void {
    try {
      this.recorder?.stop();
    } catch {
      // A recorder already stopped throws in some engines. Nothing here needs it to have worked.
    }
  }

  /**
   * Let the microphone go, and let the audio go with it.
   *
   * EVERY track, on EVERY exit path — finish, cancel, and error alike. A stream left running keeps the
   * browser's recording indicator lit and the microphone hot after the feature is visibly over, which
   * people reasonably read as the application listening to them. Of everything in this file this is the
   * part most likely to be quietly wrong, so every path goes through this one method.
   */
  private release(): void {
    for (const track of this.stream?.getTracks() ?? []) track.stop();
    this.stream = null;
    this.recorder = null;
    this.chunks = [];
    this.bytes = 0;
    this.settle = null;
    this.fail = null;
    this.state = 'idle';
  }
}

/** The real browser, wired to the interfaces above. Untested, for the reason given in the header. */
export function browserCapture(): CaptureEnvironment {
  return {
    isTypeSupported: (mimeType) =>
      typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(mimeType),
    getStream: () => navigator.mediaDevices.getUserMedia({ audio: true }),
    createRecorder: (stream, mimeType) =>
      new MediaRecorder(stream as MediaStream, { mimeType }) as unknown as CaptureRecorder,
  };
}
